import { apiClient } from '../utils/apiClient';
import type { SessionFile, AiInteraction } from '@arrowise/shared';

const MAX_EXPORT_BYTES = 25 * 1024 * 1024; // 25 MB cap for client-side zips

export async function exportSessionZip(sessionId: string): Promise<void> {
    // Fetch session export payload
    const res = await apiClient.get<{ files: SessionFile[]; aiInteractions: AiInteraction[] }>(`/sessions/${sessionId}/export`);
    if (!res || !res.data) throw new Error('Failed to fetch session export data');

    const files = res.data.files || [];
    const aiInteractions = res.data.aiInteractions || [];

    // Quick size check
    let totalBytes = 0;
    for (const f of files) {
        totalBytes += new TextEncoder().encode(f.content || '').length;
    }
    totalBytes += new TextEncoder().encode(JSON.stringify(aiInteractions)).length;
    if (totalBytes > MAX_EXPORT_BYTES) {
        throw new Error('Export too large for client-side download. Please request a server-generated export.');
    }

    // Use a web worker to build the zip off the main thread.
    // Use Vite's worker import syntax for proper bundling
    const worker = new Worker(new URL('../workers/exportWorker.ts', import.meta.url), { type: 'module' });

    const payload = { files, aiInteractions, sessionId };

    const zippedBuffer: ArrayBuffer = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Zipping timed out')), 30_000);
        worker.addEventListener('message', (ev) => {
            clearTimeout(timeout);
            const data = ev.data as any;
            if (data.error) {
                reject(new Error(data.error));
            } else if (data.buffer) {
                resolve(data.buffer as ArrayBuffer);
            } else {
                reject(new Error('Unexpected worker response'));
            }
        });
        worker.addEventListener('error', (err) => {
            clearTimeout(timeout);
            reject(err.error || err.message || err);
        });
        // Post transferable data to worker (we'll send plain objects; strings are fine)
        (worker as any).postMessage(payload);
    });

    worker.terminate();

    const blob = new Blob([zippedBuffer], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `session.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

export default { exportSessionZip };
